import { useMemo, useRef, useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useDeckStore } from '../store/deckStore';
import { CANVAS } from '../lib/brand';
import { createSmartBlock } from '../lib/layouts';
import type { SmartBlockKind } from '../types/slide';
import { slideSurfaceStyle } from '../lib/slideStyle';
import { SlideElementView } from './elements/SlideElements';
import { FloatingToolbar } from './FloatingToolbar';
import { SelectionHandles } from './SelectionHandles';

export function Canvas() {
  const slides = useDeckStore((s) => s.slides);
  const activeSlideIndex = useDeckStore((s) => s.activeSlideIndex);
  const selectedElementIds = useDeckStore((s) => s.selectedElementIds);
  const selectedElementId = useDeckStore((s) => s.selectedElementId);
  const selectElement = useDeckStore((s) => s.selectElement);
  const updateElement = useDeckStore((s) => s.updateElement);
  const addElement = useDeckStore((s) => s.addElement);
  const pushHistory = useDeckStore((s) => s.pushHistory);
  const zoom = useDeckStore((s) => s.zoom);
  const setViewScale = useDeckStore((s) => s.setViewScale);

  const slide = slides[activeSlideIndex];
  const wrapRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(0.7);
  const scale = zoom ?? fitScale;
  const [guides, setGuides] = useState<{ v?: number; h?: number }>({});
  const dragOrigin = useRef<Map<string, { x: number; y: number }> | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const pad = 80;
      const sx = (el.clientWidth - pad) / CANVAS.width;
      const sy = (el.clientHeight - pad) / CANVAS.height;
      setFitScale(Math.max(0.1, Math.min(sx, sy, 1)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => setViewScale(scale), [scale, setViewScale]);

  const selected = useMemo(
    () => slide?.elements.find((e) => e.id === selectedElementId) || null,
    [slide, selectedElementId],
  );

  if (!slide) return null;

  return (
    <Box
      ref={wrapRef}
      sx={{
        position: 'relative',
        display: 'flex',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        p: 5,
        bgcolor: 'chrome.canvas',
      }}
      onClick={(e) => {
        const t = e.target as HTMLElement;
        if (t === e.currentTarget || t.hasAttribute('data-slide-canvas')) selectElement(null);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const kind = e.dataTransfer.getData('smart-block') as SmartBlockKind | '';
        if (!kind) return;
        const rect = slideRef.current?.getBoundingClientRect();
        if (!rect) return;
        const cx = (e.clientX - rect.left) / scale;
        const cy = (e.clientY - rect.top) / scale;
        addElement(createSmartBlock(kind, { x: Math.max(40, cx - 100), y: Math.max(40, cy - 40) }));
      }}
    >
      <Box
        ref={slideRef}
        sx={{
          position: 'relative',
          flexShrink: 0,
          m: 'auto',
          boxShadow: '0 1px 3px rgba(16,24,40,0.1), 0 12px 32px -8px rgba(16,24,40,0.18)',
        }}
        style={{
          width: CANVAS.width * scale,
          height: CANVAS.height * scale,
        }}
      >
        <div
          data-slide-canvas
          style={{
            ...slideSurfaceStyle(slide),
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          {slide.elements.map((el) => (
            <SlideElementView
              key={el.id}
              el={el}
              selected={selectedElementIds.includes(el.id)}
              scale={scale}
              onSelect={selectElement}
              onDragStart={(id) => {
                pushHistory();
                const group =
                  selectedElementIds.includes(id) && selectedElementIds.length > 1
                    ? selectedElementIds
                    : [id];
                dragOrigin.current = new Map(
                  group
                    .map((gid) => {
                      const g = slide.elements.find((e) => e.id === gid);
                      return g ? ([gid, { x: g.x, y: g.y }] as const) : null;
                    })
                    .filter(Boolean) as [string, { x: number; y: number }][],
                );
              }}
              onChangeText={(id, text) => {
                if (slide.elements.find((e) => e.id === id)?.text === text) return;
                pushHistory();
                updateElement(id, { text });
              }}
              onDragEnd={(id, x, y) => {
                const target = slide.elements.find((e) => e.id === id);
                if (!target) return;
                const origins = dragOrigin.current;
                if (origins && origins.size > 1 && origins.has(id)) {
                  const origin = origins.get(id)!;
                  const dx = x - origin.x;
                  const dy = y - origin.y;
                  // Absolute positions from origins so repeated move events don't accumulate.
                  const next = useDeckStore.getState();
                  const slideNow = next.slides[next.activeSlideIndex];
                  if (!slideNow) return;
                  for (const [gid, o] of origins) {
                    const el = slideNow.elements.find((e) => e.id === gid);
                    if (!el || el.locked) continue;
                    updateElement(gid, { x: o.x + dx, y: o.y + dy });
                  }
                  return;
                }
                const midX = x + target.w / 2;
                const midY = y + target.h / 2;
                const snapV = Math.abs(midX - CANVAS.width / 2) < 12;
                const snapH = Math.abs(midY - CANVAS.height / 2) < 12;
                const snapX = snapV ? CANVAS.width / 2 - target.w / 2 : x;
                const snapY = snapH ? CANVAS.height / 2 - target.h / 2 : y;
                setGuides({
                  v: snapV ? CANVAS.width / 2 : undefined,
                  h: snapH ? CANVAS.height / 2 : undefined,
                });
                updateElement(id, { x: snapX, y: snapY });
                window.setTimeout(() => setGuides({}), 200);
              }}
            />
          ))}

          {selected && !selected.locked && selectedElementIds.length === 1 && (
            <SelectionHandles
              el={selected}
              scale={scale}
              onResizeStart={() => pushHistory()}
              onResize={(b) => updateElement(selected.id, b)}
            />
          )}

          {guides.v !== undefined && (
            <Box
              sx={{ position: 'absolute', top: 0, bottom: 0, width: '1px', bgcolor: 'primary.main', pointerEvents: 'none' }}
              style={{ left: guides.v }}
            />
          )}
          {guides.h !== undefined && (
            <Box
              sx={{ position: 'absolute', left: 0, right: 0, height: '1px', bgcolor: 'primary.main', pointerEvents: 'none' }}
              style={{ top: guides.h }}
            />
          )}
        </div>

        {selected && !selected.locked && selected.type === 'text' && selectedElementIds.length === 1 && (
          <FloatingToolbar element={selected} scale={scale} />
        )}
      </Box>
    </Box>
  );
}

/** Hidden full-size frames for PDF capture */
export function ExportFrames() {
  const slides = useDeckStore((s) => s.slides);
  return (
    <div className="pointer-events-none fixed -left-[99999px] top-0 opacity-0" aria-hidden inert>
      {slides.map((slide) => (
        <div key={slide.id} data-slide-export style={slideSurfaceStyle(slide)}>
          {slide.elements.map((el) => (
            <SlideElementView
              key={el.id}
              el={el}
              selected={false}
              scale={1}
              onSelect={() => undefined}
              onChangeText={() => undefined}
              onDragEnd={() => undefined}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
